import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { setAdminAccessToken } from '@cevop/api-client';
import { ThemeCycleButton } from '@cevop/ui';
import { authApi, notificationsApi, operationsApi } from '../api';
import { CommandPalette } from './CommandPalette';
import {
  IconAdmin,
  IconAudit,
  IconBell,
  IconBilling,
  IconInsights,
  IconMenu,
  IconOps,
  IconOrgs,
  IconOverview,
  IconPlatform,
  IconSearch,
  IconUsers,
  IconWork,
} from './Icons';
import { Avatar, BrandMark } from './BackroomChrome';
import { badgeStatus } from '../lib/format';

type NavItem = {
  to: string;
  label: string;
  permission?: string;
  end?: boolean;
  Icon: typeof IconOverview;
  badge?: boolean;
};

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', end: true, Icon: IconOverview },
  { to: '/work', label: 'Work', Icon: IconWork, badge: true },
  { to: '/orgs', label: 'Organizations', Icon: IconOrgs, permission: 'backoffice.organizations.read' },
  { to: '/users', label: 'Users', Icon: IconUsers, permission: 'backoffice.users.read' },
  { to: '/billing', label: 'Billing', Icon: IconBilling, permission: 'backoffice.billing.read' },
  { to: '/operations', label: 'Operations', Icon: IconOps, permission: 'backoffice.operations.read' },
  { to: '/insights', label: 'Insights', Icon: IconInsights, permission: 'backoffice.analytics.read' },
  { to: '/audit', label: 'Audit', Icon: IconAudit, permission: 'backoffice.audit.read' },
];

const NAV2: NavItem[] = [
  { to: '/platform', label: 'Platform', Icon: IconPlatform, permission: 'backoffice.announcements.read' },
  { to: '/administration', label: 'Administration', Icon: IconAdmin, permission: 'backoffice.team.read' },
];

function hasPermission(permissions: string[], key?: string) {
  if (!key) return true;
  return permissions.includes(key);
}

function navActive(pathname: string, to: string, end?: boolean) {
  if (end) return pathname === '/';
  if (to === '/orgs') return pathname.startsWith('/orgs');
  if (to === '/users') return pathname.startsWith('/users');
  if (to === '/audit') return pathname.startsWith('/audit');
  if (to === '/operations') return pathname.startsWith('/operations');
  return pathname === to || pathname.startsWith(`${to}/`);
}

function Sidebar({
  permissions,
  admin,
  workCount,
  onNavigate,
}: {
  permissions: string[];
  admin?: { displayName: string; email: string; role: string; mfaEnabled: boolean };
  workCount: number;
  onNavigate: () => void;
}) {
  const location = useLocation();

  return (
    <aside className="admin-sidebar sidebar-full">
      <div className="admin-sidebar__brand">
        <BrandMark />
      </div>
      <nav className="admin-sidebar__nav" aria-label="Backroom">
        <div className="sep-label">Operations</div>
        {NAV.filter((item) => hasPermission(permissions, item.permission)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={`nav-item nav-item--between${navActive(location.pathname, item.to, item.end) ? ' active' : ''}`}
            onClick={onNavigate}
          >
            <span className="nav-item__inner">
              <item.Icon />
              {item.label}
            </span>
            {item.badge && workCount > 0 && <span className="work-count">{workCount}</span>}
          </NavLink>
        ))}
        <div className="sep-label sep-label--spaced">System</div>
        {NAV2.filter((item) => hasPermission(permissions, item.permission)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={`nav-item${navActive(location.pathname, item.to) ? ' active' : ''}`}
            onClick={onNavigate}
          >
            <item.Icon />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="admin-sidebar__foot">
        {admin && (
          <div className="sidebar-user">
            <Avatar name={admin.displayName} size={24} />
            <div className="min-w-0">
              <div className="sidebar-user__name">{admin.displayName}</div>
              <div className="sidebar-user__role">{admin.role.replaceAll('_', ' ')}</div>
            </div>
          </div>
        )}
        {admin && !admin.mfaEnabled && (
          <NavLink to="/administration?tab=account" className="admin-sidebar__mfa-warn" onClick={onNavigate}>
            MFA not enabled
          </NavLink>
        )}
      </div>
    </aside>
  );
}

export function AdminShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  const meQ = useQuery({
    queryKey: ['admin-me'],
    queryFn: () => authApi.me(),
  });
  const admin = meQ.data?.administrator;
  const permissions = meQ.data?.permissions ?? [];
  const canOps = permissions.includes('backoffice.operations.read');
  const healthQ = useQuery({
    queryKey: ['operations-health'],
    queryFn: () => operationsApi.health(),
    refetchInterval: 60_000,
    enabled: canOps,
  });
  const workQ = useQuery({
    queryKey: ['work-count'],
    queryFn: async () => {
      const { workApi } = await import('../api');
      const result = await workApi.list();
      return result.data.counts.OPEN;
    },
    enabled: Boolean(meQ.data),
  });
  const notificationUnreadQ = useQuery({
    queryKey: ['notification-health-unread'],
    queryFn: async () => {
      const result = await notificationsApi.health();
      return result.data.unread;
    },
    enabled: canOps,
    refetchInterval: 60_000,
  });

  const overall = healthQ.data?.data.overallStatus ?? 'UNKNOWN';
  const degraded = badgeStatus(overall) === 'degraded' || badgeStatus(overall) === 'unavailable';

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setNavOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="admin-shell">
      {navOpen && (
        <div className="admin-nav-backdrop mobile-nav">
          <div className="mobile-nav__panel">
            <Sidebar permissions={permissions} admin={admin} workCount={workQ.data ?? 0} onNavigate={() => setNavOpen(false)} />
          </div>
          <button type="button" className="mobile-nav__close" aria-label="Close navigation" onClick={() => setNavOpen(false)} />
        </div>
      )}

      <Sidebar permissions={permissions} admin={admin} workCount={workQ.data ?? 0} onNavigate={() => setNavOpen(false)} />

      <div className="admin-main">
        <header className="admin-topbar">
          <button type="button" className="mobile-menu-btn" aria-label="Open navigation" onClick={() => setNavOpen(true)}>
            <IconMenu className="icon-18" />
          </button>
          <button type="button" className="admin-topbar__search" onClick={() => setSearchOpen(true)}>
            <IconSearch className="icon-13 icon-shrink-0" />
            <span className="topbar-search-text">Search organizations, users...</span>
            <kbd>⌘K</kbd>
          </button>
          <div className="topbar-actions">
            <div className={`status-pill${degraded ? ' status-pill--warn' : ''}`}>
              <span className={`status-dot ${degraded ? 'status-dot-amber' : 'status-dot-green'}`} />
              {degraded ? 'Degraded' : 'Operational'}
            </div>
            <ThemeCycleButton className="icon-btn" />
            <NavLink to="/operations?tab=notifications" className="icon-btn" aria-label="Notifications">
              <IconBell className="icon-16" />
              {(notificationUnreadQ.data ?? 0) > 0 && <span className="notify-dot" />}
            </NavLink>
          </div>
        </header>
        <div className="admin-content">
          <Suspense fallback={<div className="br-empty"><div className="br-empty__body">Loading…</div></div>}>
            <Outlet />
          </Suspense>
        </div>
      </div>

      {searchOpen && <CommandPalette onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

export function signOutAdmin() {
  return import('@cevop/api-client').then(async ({ adminApi }) => {
    await adminApi.post('/admin/v1/auth/logout').catch(() => {});
    setAdminAccessToken(null);
    window.location.reload();
  });
}
